import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  model,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { CustomRouteReuseStrategy } from '@services/custom-route-reuse-strategy';
import { Subscription } from 'rxjs';
import { SIDENAV_ITEMS, SIDENAV_MAPPING } from './sidenav-items';
import { MatTooltipModule } from '@angular/material/tooltip';

/** リンク項目 */
type LinkNavItem = {
  type: 'link';
  name: string;
  icon: string;
  path: string;
};

/** メニュ－項目 */
type MenuNavItem = {
  type: 'menu';
  name: string;
  icon: string;
  path: string;
  children: LinkNavItem[];
};

/** サイドメニュー項目 */
export type NavItem = LinkNavItem | MenuNavItem;

/** サイドメニュータイプ */
export type SidenavType = 'tenant' | 'admin';

/** サイドメニューの権限タイプ */
export type SidenavRoleType =
  | 'system_admin'
  | 'tenant_admin'
  | 'tenant_editor'
  | 'tenant_viewer';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatListModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.scss',
})
export class SidenavComponent implements OnInit, OnDestroy {
  @ViewChild('menuTrigger') menuTrigger?: MatMenuTrigger;

  /** 開閉状態 */
  public opened = model(true);

  /** モード */
  @Input() mode: MatDrawerMode = 'side';

  /** タイプ */
  @Input() type: SidenavType = 'tenant';

  /** 権限 */
  @Input() role: SidenavRoleType = 'tenant_viewer';

  /** 閉じたことを通知 */
  @Output() closed = new EventEmitter<void>();

  /**サイドバーのアイコンが押されたことを通知 */
  @Output() sidenavItemPushed = new EventEmitter<void>();

  /** ナビゲーション項目 */
  public navItems: NavItem[] = [];

  private breakpointSubscription: Subscription | null = null;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private routeReuseStrategy: RouteReuseStrategy,
  ) {}

  ngOnInit(): void {
    // 表示するサイドメニューの項目を設定する
    const navItems: NavItem[] = [];
    const mapping = SIDENAV_MAPPING[this.type][this.role];
    for (const key of mapping) {
      const navItem = SIDENAV_ITEMS[key];
      navItems.push(navItem);
    }
    this.navItems = navItems;

    this.breakpointSubscription = this.breakpointObserver
      .observe(Breakpoints.WebLandscape)
      .subscribe((result) => {
        // Webサイズ以上になった場合はサイドメニューを常時表示する
        // 以下の場合はサイドメニューをボタンで開けるようにする
        if (result.matches) {
          this.opened.update(() => true);
          this.mode = 'side';
        } else {
          this.opened.update(() => false);
          this.mode = 'over';
        }
      });
  }

  ngOnDestroy(): void {
    this.breakpointSubscription?.unsubscribe();
  }

  /** 閉じられた時 */
  public close() {
    this.closed.emit();
  }

  /**
   * サイドメニューのリンク押下時にルートの再利用状態をクリアする。
   */
  public onClickedLink(): void {
    this.sidenavItemPushed.emit();
    (this.routeReuseStrategy as CustomRouteReuseStrategy).refresh();
  }

  /**
   * サイドバーのメニューを開く。
   */
  public openSideNav(): void {
    this.menuTrigger?.openMenu();
  }

  /**
   * 洗濯バーのメニューを開く。
   */
  public openSelectionNav(): void {
    this.sidenavItemPushed.emit();
    this.menuTrigger?.openMenu();
  }
}
